"use client";

import React from "react";

import ScienceSectionIntro from "@/components/common/ScienceSectionIntro/ScienceSectionIntro";
import ScrollStat from "@/components/common/Stats/ScrollStat";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { segmentEvent } from "@/utils/segmentfunctions";

export type ClinicalProofProps = {
  id?: string;
  intro?: {
    tag?: string;
    headline?: string;
    text?: string;
  };
  quote?: {
    quote: string;
    author?: string;
    position?: string;
    image: { tablet: string; desktop: string; alt: string };
  };
  facts: {
    headline?: string;
    text?: string;
    stats: {
      number: number | string;
      suffix?: string;
      copy: string;
      numberClasses?: Record<string, string>;
      copyClasses?: Record<string, string>;
    }[];
  };
  learnMore?: {
    label: string;
    linkTo: string;
  };
};

export default function ClinicalProof({ intro, id, quote, facts, learnMore }: ClinicalProofProps) {
  const handleCtaClick = () => {
    if (!learnMore) return;
    segmentEvent("Marketing CTA Clicked", {
      destination: learnMore.linkTo,
      module_location: "bottom_fold",
      text: learnMore.label,
      type: "button",
    });
  };

  return (
    <section
      data-section
      id={id}
      className="relative z-[1] py-8 md:py-12 xl:py-20 bg-nova-base-white"
    >
      <div className="w-full max-w-[1440px] mx-auto md:px-8">
        {intro && <ScienceSectionIntro {...intro} headingWidth={0.55} />}

        {quote && (
          <div className="flex flex-wrap">
            <div className="w-full md:w-1/3 xl:w-1/2">
              <Picture
                src={quote.image.desktop}
                sources={[
                  {
                    media: "(min-width: 768px) and (max-width: 1279px)",
                    srcSet: quote.image.tablet,
                  },
                ]}
                alt={quote.image.alt || ""}
                role="presentation"
              />
            </div>
            <div className="w-full md:w-2/3 xl:w-1/2">
              <div className="h-full flex flex-col items-center justify-center bg-nova-base-solid-10">
                <div className="text-center py-10 px-6 md:px-10">
                  <TextElementNova
                    text={quote.quote}
                    classes={{
                      default: "Subtitle-20-Dia-Normal",
                      768: "Subtitle-20-Dia-Normal",
                    }}
                  />
                  <div className="flex flex-col gap-1 mt-10">
                    <TextElementNova text={quote.author || ""} className="Body-20-Dia-Strong" />
                    {quote.position && (
                      <TextElementNova
                        text={quote.position}
                        className="Body-10-Dia-Normal text-nova-transparency-black-70"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap flex-col md:flex-row px-6 md:px-0 pt-10 pb-2 md:pt-[88px] md:pb-12 lg:pt-20 lg:pb-0">
          <div className="w-full lg:w-[52%] xl:w-[calc(100%-578px)] flex flex-col mx-auto max-w-[355px] lg:max-w-[578px] xl:max-w-[790px]">
            {facts.headline && (
              <TextElementNova className="Title-20-Super-Normal" text={facts.headline} />
            )}
            {facts.text && (
              <TextElementNova className="Body-20-Dia-Normal pt-2" text={facts.text} />
            )}
            {learnMore && (
              <div className="hidden md:block mt-10 max-w-[293px] xl:max-w-[340px]">
                <ButtonNova
                  label={learnMore.label}
                  ctaLink={learnMore.linkTo}
                  icon="Right"
                  onClick={handleCtaClick}
                />
              </div>
            )}
          </div>
          <div className="mx-auto w-full lg:w-[48%] xl:w-[578px] py-10 lg:py-0 max-w-[355px] lg:max-w-[578px]">
            {facts.stats.map((stat, i) => (
              <ScrollStat
                key={i}
                stat={{
                  number: String(stat.number),
                  suffix: stat.suffix,
                  copy: stat.copy,
                  numberClasses: (stat.numberClasses as {
                    default: string;
                    768?: string;
                    1024?: string;
                  }) || {
                    default: "Title-30-Super-Normal",
                    768: "Title-20-Super-Normal",
                    1280: "Title-20-Super-Normal",
                  },
                }}
                statsLength={facts.stats.length}
                i={i}
                colorMode="light"
                statWidth="w-[99px] md:w-[106px] lg:w-[191px]"
                moduleName=""
              />
            ))}
          </div>
          {learnMore && (
            <div className="mx-auto md:hidden">
              <ButtonNova
                label={learnMore.label}
                ctaLink={learnMore.linkTo}
                icon="Right"
                onClick={handleCtaClick}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
